import type { SocketNotification } from '@21k/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';

export type Notification = SocketNotification;

const TOAST_DISMISS_MS = 5000;

export function useNotifications() {
  const { token } = useAuth();
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState<Notification[]>([]);
  const toastTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    const timer = toastTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      setToasts([]);
      return;
    }

    let cancelled = false;
    listNotifications()
      .then((data) => {
        if (cancelled) return;
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      })
      .catch(() => {
        /* ignore — user may not have notifications yet */
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!socket) return;

    const onNew = (n: Notification) => {
      setNotifications((prev) => {
        if (prev.some((item) => item.id === n.id)) return prev;
        return [n, ...prev];
      });
      if (!n.read) {
        setUnreadCount((prev) => prev + 1);
      }
      setToasts((prev) => (prev.some((t) => t.id === n.id) ? prev : [...prev, n]));

      const existingTimer = toastTimersRef.current.get(n.id);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        dismissToast(n.id);
      }, TOAST_DISMISS_MS);
      toastTimersRef.current.set(n.id, timer);
    };

    socket.on('notification:new', onNew);
    return () => {
      socket.off('notification:new', onNew);
    };
  }, [socket, dismissToast]);

  useEffect(() => {
    const timers = toastTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const markRead = useCallback(async (id: string) => {
    const target = notifications.find((n) => n.id === id);
    if (target && !target.read) {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    try {
      await markNotificationRead(id);
    } catch {
      if (target && !target.read) {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
        setUnreadCount((prev) => prev + 1);
      }
    }
  }, [notifications]);

  const markAllRead = useCallback(async () => {
    const previousUnread = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      setUnreadCount(previousUnread);
      listNotifications()
        .then((data) => {
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
        })
        .catch(() => undefined);
    }
  }, [unreadCount]);

  return { notifications, unreadCount, toasts, markRead, markAllRead, dismissToast };
}
